// Country code selector component

import { Box, FormControl, FormErrorMessage } from '@chakra-ui/react';
import Select, { type StylesConfig } from 'react-select';
import { COUNTRIES } from '../../client/utils/constants';

interface CountryCodeSelectProps {
  value: string;
  onChange: (code: string) => void;
  error?: string;
  isDisabled?: boolean;
  testId?: string;
}

interface CountryOption {
  value: string;
  label: string;
}

const COUNTRY_OPTIONS: CountryOption[] = COUNTRIES.map((country) => ({
  value: country.code,
  label: `${country.flag} ${country.code} ${country.name}`,
}));

// Chakra exposes the theme's semantic tokens as CSS variables, so the menu follows light and dark mode.
const color = (token: string): string => `var(--chakra-colors-${token})`;

const buildStyles = (isInvalid: boolean): StylesConfig<CountryOption, false> => ({
  control: (base, state) => ({
    ...base,
    backgroundColor: color('bg-surface'),
    borderColor: isInvalid ? color('status-error') : state.isFocused ? color('brand-500') : color('border-default'),
    boxShadow: state.isFocused ? `0 0 0 1px ${color('brand-500')}` : 'none',
    '&:hover': { borderColor: color('border-hover') },
  }),
  singleValue: (base) => ({ ...base, color: color('text-primary') }),
  input: (base) => ({ ...base, color: color('text-primary') }),
  placeholder: (base) => ({ ...base, color: color('text-muted') }),
  menu: (base) => ({ ...base, backgroundColor: color('bg-surface'), zIndex: 10 }),
  option: (base, state) => ({
    ...base,
    color: state.isSelected ? color('text-on-brand') : color('text-primary'),
    backgroundColor: state.isSelected ? color('brand-500') : state.isFocused ? color('bg-surface-hover') : 'transparent',
  }),
});

/**
 * Searchable country code selector listing every calling code; type a country name or a code
 */
export function CountryCodeSelect({
  value,
  onChange,
  error,
  isDisabled,
  testId,
}: CountryCodeSelectProps) {
  // A code set outside this list (the org API takes any) still shows as selected.
  const selected = COUNTRY_OPTIONS.find((option) => option.value === value)
    ?? (value ? { value, label: value } : null);

  return (
    <FormControl isInvalid={!!error} isDisabled={isDisabled}>
      <Box data-testid={testId}>
        <Select<CountryOption, false>
          options={COUNTRY_OPTIONS}
          value={selected}
          onChange={(option) => onChange(option?.value ?? '')}
          placeholder="Search country or code"
          isDisabled={isDisabled}
          menuPlacement="auto"
          styles={buildStyles(!!error)}
        />
      </Box>
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
}
